import { BaseResponse, QuerySchema } from "../base";
import { ProductModel } from "@/database";
import { sendReqToController } from "../network";
import { isSocketCall } from "@/utils";
import {
  VoicesResponse,
  LanguagesResponse,
  TextToAudioParams,
  TextToAudioResponse,
  TextToDialogueParams,
  TextToDialogueResponse,
  DenmarkHostModel,
} from "./model";

interface RespProduct extends BaseResponse {
  data: any;
}

interface RespProductList extends BaseResponse {
  data: ProductModel[];
  count: number;
}

interface RespDenmarkHostsList extends BaseResponse {
  data: DenmarkHostModel[];
  count: number;
}

export const GetBusinessApi = async (params: any): Promise<RespProduct> => {
  const response = await sendReqToController(
    "business.get",
    params,
    isSocketCall
  );
  try {
    const product: ProductModel = response.result;
    if (product) {
      return {
        data: product,
        error: response.error,
        status: response.code,
      };
    }
    return {
      data: {} as ProductModel,
      error: "No Product Found",
      status: response.code,
    };
  } catch {
    return {
      data: {} as ProductModel,
      error: "Failed to Validate Product Model",
      status: response.code,
    };
  }
};

export const ListDenmarkHostsApi = async (
  params: any = {}
): Promise<RespDenmarkHostsList> => {
  const query: QuerySchema = {
    size: params.rows || 1000,
    from: (!params.page ? 0 : params.page - 1) * (params.rows || 1000),
  };

  const response = await sendReqToController(
    "denmarkhosts.list",
    query,
    isSocketCall
  );
  try {
    const list: DenmarkHostModel[] = [];
    if (Array.isArray(response.result.list)) {
      response.result.list.forEach((host: DenmarkHostModel) => {
        list.push(host);
      });
    } else if (response.error) {
      response.error = "Invalid Denmark hosts list";
    }
    return {
      count: response.result.count,
      data: list,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      count: 0,
      data: [],
      error: "Failed to validate Denmark hosts list response",
      status: response.code,
    };
  }
};

export const ListBusinessApi = async (
  params: any
): Promise<RespProductList> => {
  const query: QuerySchema = {
    size: params.rows || 1000,
    from: (!params.page ? 0 : params.page - 1) * (params.rows || 50),
    foreign_keys: params.foreign_keys,
    include_archived: params?.status === undefined || params?.status === "2",
  };

  const {
    rows: _rows,
    page: _page,
    foreign_keys: _foreign_keys,
    setInRedux: _setInRedux,
    name,
    ...restParams
  } = params || {};

  query.must = {
    ...restParams,
    ...(status && status === "2" && { archive: 1 }),
    ...(name
      ? {
          "name.sat": name
            .trim()
            .toLowerCase()
            .split(/[\s_\-#]+/)
            .filter(Boolean),
        }
      : {}),
  };

  const response = await sendReqToController(
    "business.list",
    query,
    isSocketCall
  );
  try {
    const list: ProductModel[] = [];
    if (Array.isArray(response.result.list)) {
      response.result.list.forEach((product: any) => {
        list.push(product);
      });
    } else if (response.error) {
      response.error = "Invalid Business list";
    }
    return {
      count: response.result.count,
      data: list,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      count: 0,
      data: [],
      error: "Failed to validate product list response",
      status: response.code,
    };
  }
};

interface RespBunch extends BaseResponse {
  data: {
    business?: any;
    team?: any;
    category?: any;
    language?: any;
    all_business?: any;
    all_team?: any;
    default_team?: any;
    default_business?: any;
  };
  count: number;
  status: number;
}

export const ListBunchApi = async (params: any): Promise<RespBunch> => {
  const response = await sendReqToController(
    "batch.lists",
    params,
    isSocketCall
  );

  if (response?.error) {
    return {
      count: 0,
      data: [] as any,
      error: response?.error,
      status: response?.code,
    };
  }
  if (response?.result?.list?.length === 0) {
    return {
      count: 0,
      data: [] as any,
      error: "No data found",
      status: response.code,
    };
  }

  return {
    count: response?.result?.count,
    data: response?.result || [],
    error: response?.error,
    status: response?.code,
  };
};

export const UpdateBusinessApi = async (params: any): Promise<RespProduct> => {
  const response = await sendReqToController(
    "business.update",
    params,
    isSocketCall
  );
  try {
    const product: any = response.result;
    if (product) {
      return {
        data: product,
        error: response.error,
        status: response.code,
      };
    }
    return {
      data: {} as any,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: {} as any,
      error: "Failed to Validate Business Model",
      status: response.code,
    };
  }
};

export const ArchiveBusinessApi = async (params: {
  id: string;
  archive: number;
}): Promise<RespProduct> => {
  const response = await sendReqToController(
    "business.archive",
    params,
    isSocketCall
  );
  try {
    const business: any = response.result;
    if (business) {
      return { data: business, error: response.error, status: response.code };
    }
    return { data: {} as any, error: response.error, status: response.code };
  } catch {
    return {
      data: {} as any,
      error: "Failed to Validate Business Model",
      status: response.code,
    };
  }
};

export const AddBusinessApi = async (params: any): Promise<RespProduct> => {
  const response = await sendReqToController(
    "business.create",
    params,
    isSocketCall
  );
  try {
    const business: ProductModel = response.result;
    if (business) {
      return {
        data: business,
        error: response.error,
        status: response.code,
      };
    }
    return {
      data: {} as ProductModel,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: {} as ProductModel,
      error: "Failed to Add Product",
      status: response.code,
    };
  }
};

export const GetProductApi = async (params: any): Promise<RespProduct> => {
  const response = await sendReqToController("team.get", params, isSocketCall);
  try {
    const product: ProductModel = response.result;
    if (product) {
      return {
        data: product,
        error: response.error,
        status: response.code,
      };
    }
    return {
      data: {} as ProductModel,
      error: "No Product Found",
      status: response.code,
    };
  } catch {
    return {
      data: {} as ProductModel,
      error: "Failed to Validate Product Model",
      status: response.code,
    };
  }
};

export const ListProductApi = async (params: any): Promise<RespProductList> => {
  const query: QuerySchema = {
    size: params.rows || 1000,
    from: (!params.page ? 0 : params.page - 1) * (params.rows || 50),
    foreign_keys: params.foreign_keys,
    include_archived: params?.status === undefined || params?.status === "2",
  };

  const {
    rows: _rows,
    page: _page,
    foreign_keys: _foreign_keys,
    status: _status,
    name,
    ...restParams
  } = params || {};

  query.must = {
    ...(_status && _status === "2" && { archive: 1 }),
    ...restParams,
    ...(name
      ? {
          "name.sat": name
            .trim()
            .toLowerCase()
            .split(/[\s_\-#]+/)
            .filter(Boolean),
        }
      : {}),
  };

  const response = await sendReqToController("team.list", query, isSocketCall);
  try {
    const list: ProductModel[] = [];
    if (Array.isArray(response.result.list)) {
      response.result.list.forEach((product: any) => {
        list.push(product);
      });
    } else if (response.error) {
      response.error = "Invalid product list";
    }
    return {
      count: response.result.count,
      data: list,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      count: 0,
      data: [],
      error: "Failed to validate product list response",
      status: response.code,
    };
  }
};

export const UpdateProductApi = async (params: any): Promise<RespProduct> => {
  const response = await sendReqToController(
    "team.update",
    params,
    isSocketCall
  );
  try {
    const product: any = response.result;
    if (product) {
      return {
        data: product,
        error: response.error,
        status: response.code,
      };
    }
    return {
      data: {} as any,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: {} as any,
      error: "Failed to Validate product Model",
      status: response.code,
    };
  }
};

export const ArchiveProductApi = async (params: {
  id: string;
  archive: number;
}): Promise<RespProduct> => {
  const response = await sendReqToController(
    "team.archive",
    params,
    isSocketCall
  );
  try {
    const product: any = response.result;
    if (product) {
      return {
        data: product,
        error: response.error,
        status: response.code,
      };
    }
    return {
      data: {} as any,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: {} as any,
      error: "Failed to Validate product Model",
      status: response.code,
    };
  }
};

export const AddProductApi = async (params: any): Promise<RespProduct> => {
  const response = await sendReqToController(
    "team.create",
    params,
    isSocketCall
  );
  try {
    const product: ProductModel = response.result;
    if (product) {
      return {
        data: product,
        error: response.error,
        status: response.code,
      };
    }
    return {
      data: {} as ProductModel,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: {} as ProductModel,
      error: "Failed to Add Product",
      status: response.code,
    };
  }
};

export const GetAgenciesApi = async (params: any): Promise<RespProduct> => {
  const response = await sendReqToController(
    "agency.get",
    params,
    isSocketCall
  );
  try {
    const agencies: ProductModel = response.result;
    if (agencies) {
      return {
        data: agencies,
        error: response.error,
        status: response.code,
      };
    }
    return {
      data: {} as ProductModel,
      error: "No agencies Found",
      status: response.code,
    };
  } catch {
    return {
      data: {} as ProductModel,
      error: "Failed to Validate agencies Model",
      status: response.code,
    };
  }
};

export const ListAgenciesApi = async (
  params: any
): Promise<RespProductList> => {
  const query: QuerySchema = {
    size: params?.rows || 50,
    from: (!params?.page ? 0 : params?.page - 1) * (params?.rows || 50),
    foreign_keys: params?.foreign_keys,
    include_archived: params?.status === undefined || params?.status === "2",
  };

  // Create a new object without rows, page, and foreign_keys properties
  const {
    rows: _rows,
    page: _page,
    foreign_keys: _foreign_keys,
    setInRedux: _setInRedux,
    status: _status,
    name,
    ...restParams
  } = params || {};

  query.must = {
    ...(_status && _status === "2" && { archive: 1 }),
    ...restParams,
    ...(name
      ? {
          "name.sat": name
            .trim()
            .toLowerCase()
            .split(/[\s_\-#]+/)
            .filter(Boolean),
        }
      : {}),
  };

  if (query.must?.status) {
    query.must.status = Number(query.must.status);
  }

  query.sort = {
    last_7d_video_count: "desc",
    video_count: "desc",
    last_video_ct: "desc",
  };

  query.sort_priority = ["last_7d_video_count", "video_count", "last_video_ct"];

  const response = await sendReqToController(
    "agency.list",
    query,
    isSocketCall
  );
  try {
    const list: ProductModel[] = [];
    if (Array.isArray(response.result.list)) {
      response.result.list.forEach((agencies: any) => {
        list.push(agencies);
      });
    } else if (response.error) {
      response.error = "Invalid agencies list";
    }
    return {
      count: response.result.count,
      data: list,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      count: 0,
      data: [],
      error: "Failed to validate agencies list response",
      status: response.code,
    };
  }
};

export const UpdateAgenciesApi = async (params: any): Promise<RespProduct> => {
  const response = await sendReqToController(
    "agency.update",
    params,
    isSocketCall
  );
  try {
    const agencies: any = response.result;
    if (agencies) {
      return {
        data: agencies,
        error: response.error,
        status: response.code,
      };
    }
    return {
      data: {} as any,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: {} as any,
      error: "Failed to Validate agencies Model",
      status: response.code,
    };
  }
};

export const ArchiveAgencyApi = async (params: {
  id: string;
  archive: number;
}): Promise<RespProduct> => {
  const response = await sendReqToController(
    "agency.archive",
    params,
    isSocketCall
  );
  try {
    const agency: any = response.result;
    if (agency) {
      return { data: agency, error: response.error, status: response.code };
    }
    return { data: {} as any, error: response.error, status: response.code };
  } catch {
    return {
      data: {} as any,
      error: "Failed to Validate Agency Model",
      status: response.code,
    };
  }
};

export const AddAgenciesApi = async (params: any): Promise<RespProduct> => {
  const response = await sendReqToController(
    "agency.create",
    params,
    isSocketCall
  );
  try {
    const agencies: ProductModel = response.result;
    if (agencies) {
      return {
        data: agencies,
        error: response.error,
        status: response.code,
      };
    }
    return {
      data: {} as ProductModel,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: {} as ProductModel,
      error: "Failed to Add agencies",
      status: response.code,
    };
  }
};

export const GetOrganizationApi = async (params: any): Promise<RespProduct> => {
  const response = await sendReqToController(
    "organization.get",
    params,
    isSocketCall
  );
  try {
    const organization: ProductModel = response.result;
    if (organization) {
      return {
        data: organization,
        error: response.error,
        status: response.code,
      };
    }
    return {
      data: {} as ProductModel,
      error: "No organization Found",
      status: response.code,
    };
  } catch {
    return {
      data: {} as ProductModel,
      error: "Failed to Validate organization Model",
      status: response.code,
    };
  }
};

export const ListOrganizationApi = async (
  params: any
): Promise<RespProductList> => {
  const query: QuerySchema = {
    size: params.rows || 50,
    from: (!params.page ? 0 : params.page - 1) * (params.rows || 50),
    foreign_keys: params.foreign_keys,
  };
  delete params.rows;
  delete params.page;
  delete params.foreign_keys;
  query.must = {
    ...params,
  };

  const response = await sendReqToController(
    "organization.list",
    query,
    isSocketCall
  );
  try {
    const list: ProductModel[] = [];
    if (Array.isArray(response.result.list)) {
      response.result.list.forEach((organization: any) => {
        list.push(organization);
      });
    } else if (response.error) {
      response.error = "Invalid organization list";
    }
    return {
      count: response.result.count,
      data: list,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      count: 0,
      data: [],
      error: "Failed to validate organization list response",
      status: response.code,
    };
  }
};

export const UpdateOrganizationApi = async (
  params: any
): Promise<RespProduct> => {
  const response = await sendReqToController(
    "organization.update",
    params,
    isSocketCall
  );
  try {
    const organization: any = response.result;
    if (organization) {
      return {
        data: organization,
        error: response.error,
        status: response.code,
      };
    }
    return {
      data: {} as any,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: {} as any,
      error: "Failed to Validate Organization Model",
      status: response.code,
    };
  }
};

export const AddOrganizationApi = async (params: any): Promise<RespProduct> => {
  const response = await sendReqToController(
    "organization.create",
    params,
    isSocketCall
  );
  try {
    const organization: ProductModel = response.result;
    if (organization) {
      return {
        data: organization,
        error: response.error,
        status: response.code,
      };
    }
    return {
      data: {} as ProductModel,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: {} as ProductModel,
      error: "Failed to Add Organization",
      status: response.code,
    };
  }
};

export const UpdateSocialChannelsApi = async (
  params: any
): Promise<RespProduct> => {
  const response = await sendReqToController(
    "socialconnect.update",
    params,
    isSocketCall
  );
  try {
    const socialChannels: any = response.result;
    if (socialChannels) {
      return {
        data: socialChannels,
        error: response.error,
        status: response.code,
      };
    }
    return {
      data: {} as any,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: {} as any,
      error: "Failed to Validate socialChannels Model",
      status: response.code,
    };
  }
};

export const DeleteSocialChannelsApi = async (
  params: any
): Promise<RespProduct> => {
  const response = await sendReqToController(
    "socialconnect.delete",
    params,
    isSocketCall
  );
  try {
    const socialChannels: any = response.result;
    if (socialChannels) {
      return {
        data: socialChannels,
        error: response.error,
        status: response.code,
      };
    }
    return {
      data: {} as any,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: {} as any,
      error: "Failed to Validate socialChannels Model",
      status: response.code,
    };
  }
};

export const DeleteTranscriptApi = async (
  params: any
): Promise<RespProduct> => {
  const response = await sendReqToController(
    "transcription.delete",
    params,
    isSocketCall
  );
  try {
    const transcript: any = response.result;
    if (transcript) {
      return {
        data: transcript,
        error: response.error,
        status: response.code,
      };
    }
    return {
      data: {} as any,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: {} as any,
      error: "Failed to Validate transcript Model",
      status: response.code,
    };
  }
};

export const TranslateTitleApi = async (params: any): Promise<RespProduct> => {
  const response = await sendReqToController(
    "ai.service",
    params,
    isSocketCall
  );
  try {
    const translateTitle: any = response.result;
    if (translateTitle) {
      return {
        data: translateTitle,
        error: response.error,
        status: response.code,
      };
    }
    return {
      data: {} as any,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: {} as any,
      error: "Failed to Validate translateTitle Model",
      status: response.code,
    };
  }
};

export const DeleteSocialPublishApi = async (
  params: any
): Promise<RespProduct> => {
  const response = await sendReqToController(
    "socialpublish.delete",
    params,
    isSocketCall
  );
  try {
    const socialPublish: any = response.result;
    if (socialPublish) {
      return {
        data: socialPublish,
        error: response.error,
        status: response.code,
      };
    }
    return {
      data: {} as any,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: {} as any,
      error: "Failed to Validate socialChannels Model",
      status: response.code,
    };
  }
};

export const TakedownSocialPublishApi = async (
  params: any
): Promise<RespProduct> => {
  const response = await sendReqToController(
    "socialpublish.takedown",
    params,
    isSocketCall
  );
  try {
    const socialPublish: any = response.result;
    if (socialPublish) {
      return {
        data: socialPublish,
        error: response.error,
        status: response.code,
      };
    }
    return {
      data: {} as any,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: {} as any,
      error: "Failed to Validate socialChannels Model",
      status: response.code,
    };
  }
};

export const GetAiVoicesApi = async (params?: {
  key?: string;
}): Promise<{
  data: VoicesResponse | null;
  error: string;
  status: number;
}> => {
  const response = await sendReqToController(
    "ai.voices",
    params ?? {},
    isSocketCall
  );
  try {
    const voices: VoicesResponse = response.result?.normalized
      ? response.result
      : response.result;
    if (voices) {
      return {
        data: voices,
        error: response.error,
        status: response.code,
      };
    }
    return {
      data: null,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: null,
      error: "Failed to fetch AI voices",
      status: response.code,
    };
  }
};

export const TextToAudioApi = async (
  params: TextToAudioParams
): Promise<{
  data: TextToAudioResponse | null;
  error: string;
  status: number;
}> => {
  const response = await sendReqToController(
    "ai.text_to_audio",
    params,
    isSocketCall
  );
  try {
    const result: TextToAudioResponse = {
      normalized: response.result?.normalized,
      output: response.result?.output,
    };
    if (result.normalized) {
      return {
        data: result,
        error: response.error,
        status: response.code,
      };
    }
    return {
      data: null,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: null,
      error: "Failed to convert text to audio",
      status: response.code,
    };
  }
};

export const TextToDialogueApi = async (
  params: TextToDialogueParams
): Promise<{
  data: TextToDialogueResponse | null;
  error: string;
  status: number;
}> => {
  const response = await sendReqToController(
    "ai.text_to_audio_multi",
    params,
    isSocketCall
  );
  try {
    const result: TextToDialogueResponse = {
      normalized: response.result?.normalized,
      output: response.result?.output,
    };
    if (result.normalized) {
      return { data: result, error: response.error, status: response.code };
    }
    return { data: null, error: response.error, status: response.code };
  } catch {
    return {
      data: null,
      error: "Failed to convert text to dialogue audio",
      status: response.code,
    };
  }
};

export const GetAiLanguagesApi = async (params?: {
  key?: string;
}): Promise<{
  data: LanguagesResponse | null;
  error: string;
  status: number;
}> => {
  const response = await sendReqToController(
    "ai.languages",
    params ?? {},
    isSocketCall
  );
  try {
    const languages: LanguagesResponse = response.result;
    if (languages) {
      return {
        data: languages,
        error: response.error,
        status: response.code,
      };
    }
    return {
      data: null,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: null,
      error: "Failed to fetch AI languages",
      status: response.code,
    };
  }
};

export const ListMediaLogsApi = async (params: any): Promise<any> => {
  const query: QuerySchema = {
    size: params.rows || 50,
    from: (!params.page ? 0 : params.page - 1) * (params.rows || 50),
    sort: {
      ct: "desc",
    },
  };
  const { rows: _rows, page: _page, range, system, ...must } = params;
  if (range) {
    query.range = range;
  }
  if (system) {
    query.system = true;
  }
  query.must = {
    ...must,
  };

  const response = await sendReqToController("logs.list", query, isSocketCall);

  try {
    const list: any[] = [];
    if (Array.isArray(response.result.list)) {
      response.result.list.forEach((mediaLogs: any) => {
        list.push(mediaLogs);
      });
    }
    return {
      count: response.result.count,
      data: list,
      userContributorData: response.result.userContributorData ?? [],
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      count: 0,
      data: [],
      userContributorData: [],
      error: "Failed to validate media logs list response",
    };
  }
};
